import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { 
  Target, 
  Clock, 
  Plus, 
  Trash2, 
  AlertCircle,
  Loader2,
  CheckCircle2,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SLAConfigPage() {
  const [slaConfigs, setSlaConfigs] = useState([]);
  const [slaStatuses, setSlaStatuses] = useState({});
  const [hostGroups, setHostGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newSla, setNewSla] = useState({
    name: '',
    group_ids: [],
    target_availability: 99.9,
    target_mttr_hours: 4
  });
  const { getAuthHeader, user } = useAuthStore();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [slaResponse, groupsResponse] = await Promise.all([
        axios.get(`${API}/sla-configs`, { headers: getAuthHeader() }),
        axios.get(`${API}/hostgroups`, { headers: getAuthHeader() })
      ]);
      const configs = slaResponse.data;
      setSlaConfigs(configs);
      setHostGroups(groupsResponse.data.groups || []);
      setError(null);
      
      // Fetch SLA status for each config
      configs.forEach(async (sla) => {
        try {
          const res = await axios.get(`${API}/sla-configs/${sla.id}/status?days=30`, { headers: getAuthHeader() });
          setSlaStatuses(prev => ({ ...prev, [sla.id]: res.data }));
        } catch (err) {
          console.warn(`Erro ao carregar status do SLA ${sla.id}:`, err);
          setSlaStatuses(prev => ({ ...prev, [sla.id]: { error: true } }));
        }
      });
    } catch (err) {
      setError('Erro ao carregar configurações de SLA');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async () => {
    if (!newSla.name || newSla.group_ids.length === 0) {
      toast.error('Preencha o nome e selecione ao menos um grupo');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/sla-configs`, newSla, { headers: getAuthHeader() });
      toast.success('SLA criado com sucesso');
      setDialogOpen(false);
      setNewSla({
        name: '',
        group_ids: [],
        target_availability: 99.9,
        target_mttr_hours: 4
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar SLA');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este SLA?')) return;

    try {
      await axios.delete(`${API}/sla-configs/${id}`, { headers: getAuthHeader() });
      toast.success('SLA excluído');
      fetchData();
    } catch (err) {
      toast.error('Erro ao excluir SLA');
    }
  };

  const toggleGroupSelection = (groupId) => {
    setNewSla(prev => ({
      ...prev,
      group_ids: prev.group_ids.includes(groupId)
        ? prev.group_ids.filter(id => id !== groupId)
        : [...prev.group_ids, groupId]
    }));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sla-config-page">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Configurações de SLA</h2>
          <p className="text-sm text-muted-foreground">
            Defina metas de disponibilidade e tempo de resposta por grupo de hosts
          </p>
        </div>
        {user?.role === 'admin' && (
          <Button onClick={() => setDialogOpen(true)} data-testid="add-sla-btn">
            <Plus className="h-4 w-4 mr-2" />
            Novo SLA
          </Button>
        )}
      </div>

      {/* SLA Cards */}
      {slaConfigs.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium">Nenhum SLA configurado</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Crie configurações de SLA para monitorar a disponibilidade dos seus hosts
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {slaConfigs.map(sla => {
            const status = slaStatuses[sla.id];
            const summary = status?.summary;
            const isLoading = !status;
            const meetsAvail = summary?.meets_availability_target;
            const meetsMttr = summary?.meets_mttr_target;
            
            return (
            <Card key={sla.id} data-testid={`sla-card-${sla.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{sla.name}</CardTitle>
                    {summary && (
                      <Badge variant={meetsAvail && meetsMttr ? "default" : "destructive"} className="mt-1">
                        {meetsAvail && meetsMttr ? "Dentro do SLA" : "Fora do SLA"}
                      </Badge>
                    )}
                  </div>
                  {user?.role === 'admin' && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(sla.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Real vs Target availability */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <TrendingUp className="h-3 w-3" />
                      Disponibilidade Real
                    </div>
                    {isLoading ? <Skeleton className="h-7 w-20" /> : (
                      <p className={`text-xl font-bold ${meetsAvail ? 'text-green-600' : 'text-red-600'}`}>
                        {summary?.availability?.toFixed(2) ?? '—'}%
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">Meta: {sla.target_availability}%</p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      MTTR Real
                    </div>
                    {isLoading ? <Skeleton className="h-7 w-20" /> : (
                      <p className={`text-xl font-bold ${meetsMttr ? 'text-green-600' : 'text-red-600'}`}>
                        {summary?.mttr_hours?.toFixed(1) ?? '0'}h
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">Meta: {sla.target_mttr_hours}h</p>
                  </div>
                </div>

                {/* Stats */}
                {summary && (
                  <div className="grid grid-cols-3 gap-2 text-center border-t pt-3">
                    <div>
                      <p className="text-lg font-semibold">{summary.total_hosts}</p>
                      <p className="text-xs text-muted-foreground">Hosts</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold">{summary.total_incidents}</p>
                      <p className="text-xs text-muted-foreground">Incidentes</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold">{summary.compliance_rate}%</p>
                      <p className="text-xs text-muted-foreground">Compliance</p>
                    </div>
                  </div>
                )}

                {/* Groups */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Grupos monitorados:</p>
                  <div className="flex flex-wrap gap-1">
                    {sla.group_ids.map(groupId => {
                      const group = hostGroups.find(g => g.id === groupId);
                      return (
                        <Badge key={groupId} variant="secondary" className="text-xs">
                          {group?.name || groupId}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          );})}
        </div>
      )}

      {/* Create SLA Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Criar Configuração de SLA</DialogTitle>
            <DialogDescription>
              Defina metas de disponibilidade para um ou mais grupos de hosts
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome do SLA</Label>
              <Input
                value={newSla.name}
                onChange={(e) => setNewSla({ ...newSla, name: e.target.value })}
                placeholder="Ex: Servidores de Produção"
                data-testid="sla-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Meta de Disponibilidade (%)</Label>
                <Select 
                  value={String(newSla.target_availability)}
                  onValueChange={(v) => setNewSla({ ...newSla, target_availability: parseFloat(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="99">99%</SelectItem>
                    <SelectItem value="99.5">99.5%</SelectItem>
                    <SelectItem value="99.9">99.9%</SelectItem>
                    <SelectItem value="99.95">99.95%</SelectItem>
                    <SelectItem value="99.99">99.99%</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>MTTR Máximo (horas)</Label>
                <Select 
                  value={String(newSla.target_mttr_hours)}
                  onValueChange={(v) => setNewSla({ ...newSla, target_mttr_hours: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 hora</SelectItem>
                    <SelectItem value="2">2 horas</SelectItem>
                    <SelectItem value="4">4 horas</SelectItem>
                    <SelectItem value="8">8 horas</SelectItem>
                    <SelectItem value="24">24 horas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Grupos de Hosts</Label>
              <div className="border rounded-lg max-h-48 overflow-y-auto p-2">
                {hostGroups.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Configure a API Zabbix para ver os grupos
                  </p>
                ) : (
                  <div className="space-y-1">
                    {hostGroups.map(group => (
                      <button
                        key={group.id}
                        type="button"
                        onClick={() => toggleGroupSelection(group.id)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-colors ${
                          newSla.group_ids.includes(group.id)
                            ? 'bg-primary/10 text-primary'
                            : 'hover:bg-accent'
                        }`}
                      >
                        <span>{group.name}</span>
                        {newSla.group_ids.includes(group.id) && (
                          <CheckCircle2 className="h-4 w-4" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {newSla.group_ids.length} grupo(s) selecionado(s)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Criar SLA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
